import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, Globe2, Heart, Leaf, Sparkles, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useTestRide } from "../components/TestRideContext";

export default function About() {
  const { openModal } = useTestRide();
  return (
    <div data-testid="about-page" className="bg-white">
      <section className="relative pt-16 pb-20 border-b border-slate-200 overflow-hidden bg-gradient-to-b from-[#F0F6FF] to-white">
        <div className="absolute inset-0 helix-bg" />
        <div className="container-x relative">
          <p className="eyebrow">About eBikeGo</p>
          <h1 className="font-outfit text-5xl lg:text-7xl xl:text-8xl font-bold mt-5 leading-[0.98] tracking-tighter max-w-5xl text-slate-900">
            We move India,<br />
            <span className="bg-gradient-to-r from-[#15B47E] to-[#2A8BFF] bg-clip-text text-transparent">one e-cycle at a time.</span>
          </h1>
          <p className="text-slate-600 text-lg mt-6 max-w-2xl">
            eBikeGo Store is an integrated electric-cycle platform — rental, retail and service in one network — built for Indian streets, Indian distances and Indian wallets.
          </p>
        </div>
      </section>

      <section id="mission" className="container-x py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow">Our mission</p>
            <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
              Cleaner cities. <br />
              <span className="text-slate-400">Higher earnings.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 text-slate-700 space-y-5 text-lg leading-relaxed">
            <p>
              We started eBikeGo because India's mobility maths is broken. Two-wheeler fuel costs ₹6,000–₹8,000 per month for a gig rider. An e-cycle? Less than a fifth.
            </p>
            <p>
              We are a manufacturer-backed network running rental, retail and service under one roof. From a college student in Pune to a delivery rider in Whitefield, every customer gets the same trained technician, same genuine spares and the same 24×7 helpline.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F8FB] border-y border-slate-200 py-20">
        <div className="container-x">
          <p className="eyebrow">What we believe</p>
          <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
            Six principles that shape every store.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              { icon: <Award size={20} />, title: "Manufacturer-backed", text: "Lifetime frame warranty. 2 yrs on motor. Every cycle." },
              { icon: <Globe2 size={20} />, title: "Built for India", text: "Tested across monsoons, mountains, and Mumbai gridlock." },
              { icon: <Heart size={20} />, title: "Rider-first economics", text: "We obsess over the ₹ per km, not over MRP." },
              { icon: <Sparkles size={20} />, title: "Service at the centre", text: "Every store is a service centre. Not an afterthought." },
              { icon: <Leaf size={20} />, title: "Quietly sustainable", text: "Two-wheeler emissions, but without the noise or fuel." },
              { icon: <Users size={20} />, title: "Empowering gig work", text: "Lowering the cost of earning a living, one ride at a time." },
            ].map((v) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 rounded-2xl p-7"
              >
                <span className="w-10 h-10 rounded-full bg-[#3FE0A6]/15 text-[#15B47E] inline-flex items-center justify-center">{v.icon}</span>
                <h4 className="font-outfit font-bold text-lg mt-4 text-slate-900">{v.title}</h4>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1675798227643-da319f8ee8f7?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Service centre"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="eyebrow">Trusted by riders, daily</p>
            <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
              Numbers that ride for us.
            </h2>
            <div className="mt-10 grid grid-cols-2 gap-4">
              {[
                { v: "25,000+", l: "Support tickets resolved" },
                { v: "4.8 / 5", l: "Rider service rating" },
                { v: "10,000+", l: "Test rides per month" },
                { v: "65+", l: "Stores across India" },
              ].map((s) => (
                <div key={s.l} className="bg-white border border-slate-200 rounded-2xl p-6">
                  <p className="font-outfit text-4xl font-bold text-slate-900">{s.v}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-2 font-bold">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F8FB] border-y border-slate-200 py-20">
        <div className="container-x">
          <p className="eyebrow">Voices from the street</p>
          <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
            What riders say.
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {[
              { quote: "₹3,200 saved every month versus my old petrol scooter. The Whitefield store services my Flare X like clockwork.",
                name: "Arjun K.", role: "Gig rider · Bengaluru" },
              { quote: "I bought a Momentum for my commute to Andheri. Premium ride, removable battery, and the store team feels like family.",
                name: "Neha S.", role: "Daily commuter · Mumbai" },
            ].map((t) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 rounded-2xl p-8"
              >
                <p className="text-2xl font-outfit text-slate-900 leading-snug">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="font-bold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="franchise" className="container-x py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow">Franchise / Dealership</p>
            <h2 className="font-outfit text-4xl lg:text-5xl font-bold mt-4 leading-[0.98] text-slate-900">
              Open an eBikeGo Store in your city.
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed max-w-xl">
              Run rental, retail and service from day one with a turnkey playbook. Manufacturer-backed inventory, training and marketing support for your store. Flexible commercials — <span className="font-semibold text-slate-800">2% monthly minimum or 5% revenue share, as per our FICO model.</span>
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <button onClick={() => openModal({ variant: "partner" })} className="btn-primary" data-testid="franchise-cta">
                Become a Partner
              </button>
              <Link to="/contact" className="btn-outline">
                Download Brochure <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { k: "₹50L–₹75L", v: "Indicative setup" },
              { k: "2% / 5%", v: "Monthly min or revenue share" },
              { k: "60+", v: "Active partners" },
              { k: "Tier 1–3", v: "Cities supported" },
            ].map((s) => (
              <div key={s.v} className="bg-white border border-slate-200 rounded-2xl p-6">
                <p className="font-outfit text-3xl font-bold text-slate-900">{s.k}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-2 font-bold">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
